import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { SaveIcon } from "../../packages/SVGs";
import { validationRangeHardError } from "../../packages/WPInput/WPRangedInput";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import { useToastNotification } from "../../packages/WPToastNotification/WPToastNotificationProvider";

import CIPSequence from "./CIPSequence";
import {
  getCIPSequenceIdFromSequenceTextArr,
  getCIPSequenceTextArrFromCIPSequenceID,
  normalizeAdditiveProcessFieldsForSave,
} from "./cipSequence.utils";
import { INITIAL_DEFAULT_CIP_ERROR_STATE } from "./defaultCIPInformationFields.config";

import {
  DEFAULT_CIP_AMOUNT_PER_ELEMENT,
  DEFAULT_CIP_API_KEY_MAP,
  STAGE_DEFAULTS,
} from "../../constants/global.constants";

import { NewBadgeIcon } from "../../assets/icons";

import CircularLoader from "../../common/styles/components/circularLoader";
import IconWithTooltip from "../../common/styles/components/headings/IconWithTooltip";

import { isNullOrUndefined } from "../../utils/appUtils";
import { getLocalStorageItem } from "../../utils/session";
import { unitConverter } from "../../utils/Utils";

import {
  editDefaultCIPValues,
  getDefaultCIPValues,
} from "../../network/cipAPI";

import { storeDefaultInformation } from "../../Store/TemplateStore/template.action";

import "./index.scss";

const normalizeDefaultCIPPayloadForApi = (payload = {}) => {
  return Object.keys(payload).reduce((acc, key) => {
    const apiKey = DEFAULT_CIP_API_KEY_MAP[key] || key;
    acc[apiKey] = payload[key];
    return acc;
  }, {});
};

const DefaultCIPInformation = React.forwardRef(
  ({
    selectedTrain,
    disabled,
    fileId,
    setMandatoryComponentsStatus,
    noOfRONFElements,
    selectedPass,
    selectedStage,
  }) => {
    const { showSuccessNotification, showErrorNotification } =
      useToastNotification();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { mass, temperature, cips, defaultCIPValues, plantChemicalLibrary } =
      useSelector((state) => ({
        mass: state.mass,
        temperature: state.temperature,
        cips: state.cips,
        plantChemicalLibrary: state.plantChemicalLibrary,
        defaultCIPValues: {
          cip_sequence: state.cip_sequence,
          acid_price: state.acid_price,
          alkali_price: state.alkali_price,
          additive_alkali_price: state.additive_alkali_price,
          additive_acid_price: state.additive_acid_price,
          acid_chem_id: state.acid_chem_id,
          alkali_chem_id: state.alkali_chem_id,
          additive_alkali_chem_id: state.additive_alkali_chem_id,
          additive_acid_chem_id: state.additive_acid_chem_id,
          acid_used_per_cip: state.acid_used_per_cip,
          alkali_used_per_cip: state.alkali_used_per_cip,
          additive_alkali_used_per_cip: state.additive_alkali_used_per_cip,
          additive_acid_used_per_cip: state.additive_acid_used_per_cip,
          acid_cip_ph: state.acid_cip_ph,
          alkali_cip_ph: state.alkali_cip_ph,
          additive_alkali_ph: state.additive_alkali_ph,
          additive_acid_ph: state.additive_acid_ph,
          acid_cip_temp: state.acid_cip_temp,
          alkali_cip_temp: state.alkali_cip_temp,
          additive_alkali_temp: state.additive_alkali_temp,
          additive_acid_temp: state.additive_acid_temp,
          acid_cip_duration: state.acid_cip_duration,
          alkali_cip_duration: state.alkali_cip_duration,
          additive_alkali_duration: state.additive_alkali_duration,
          additive_acid_duration: state.additive_acid_duration,
        },
      }));
    const [changedCIPValues, setChangedCIPValues] = useState({});
    const [errors, setErrors] = useState(INITIAL_DEFAULT_CIP_ERROR_STATE);
    const [hasMissingRequiredFields, setHasMissingRequiredFields] =
      useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const changedCIPValuesRef = useRef(changedCIPValues);
    const defaultCIPValuesRef = useRef(defaultCIPValues);
    const hasDefaultCIPInitialMount = useRef(false);

    const [cipSequenceTextArr, setCIPSequenceTextArr] = useState(
      getCIPSequenceTextArrFromCIPSequenceID(defaultCIPValues?.cip_sequence),
    );

    const noError = useMemo(
      () => Object.values(errors).every((error) => error === null),
      [errors],
    );

    const getFieldValidationError = useCallback(
      (value, minValue, maxValue) => {
        if (isNullOrUndefined(minValue) || isNullOrUndefined(maxValue)) {
          return null;
        }

        const normalizedValue =
          value === null || value === undefined ? "" : String(value).trim();

        if (normalizedValue === "") {
          return t("ValueStrings.requiredField");
        }

        const numericValue = Number(normalizedValue);
        if (Number.isNaN(numericValue)) {
          return t("ValueStrings.requiredField");
        }

        const hardError = validationRangeHardError(t, numericValue, [
          minValue,
          maxValue,
        ]);

        return hardError?.error || null;
      },
      [t],
    );

    const handleInputChange = (field, value, minValue, maxValue) => {
      setChangedCIPValues((prev) => ({ ...prev, [field]: value }));

      setErrors((prev) => ({
        ...prev,
        [field]: getFieldValidationError(value, minValue, maxValue),
      }));
    };

    const handleSectionFieldChange = (field, value) => {
      setChangedCIPValues((prev) => ({ ...prev, [field]: value }));
    };

    // Helper to fetch and restore last saved values
    const refreshDefaultCIPFromServer = async () => {
      try {
        const res = await getDefaultCIPValues(selectedTrain);
        const defaultInfo = res?.data;
        if (defaultInfo) {
          dispatch(storeDefaultInformation(defaultInfo));
        }
      } catch (getError) {
        showErrorNotification(t("defaultInformationStrings.fetchFailed"));
      }
    };

    const getSaveButtonState = () => {
      const hasChanges = Object.keys(changedCIPValues).length > 0;
      return !(hasChanges && noError && !hasMissingRequiredFields);
    };

    const handleSave = async (newChangedValues = {}) => {
      const hasAutoSaveValues = Object.keys(newChangedValues).length > 0;
      const changedValuesToSave = hasAutoSaveValues
        ? { ...changedCIPValues, ...newChangedValues }
        : changedCIPValues;

      const syncedChangedValuesToSave = normalizeAdditiveProcessFieldsForSave(
        changedValuesToSave,
        defaultCIPValues,
      );

      const payload = {
        ...normalizeDefaultCIPPayloadForApi(syncedChangedValuesToSave),
        cip_sequence: getCIPSequenceIdFromSequenceTextArr(cipSequenceTextArr),
      };

      if (Object.keys(payload).length === 0) {
        return;
      }

      setIsSaving(true);
      try {
        const editRes = await editDefaultCIPValues(selectedTrain, payload);
        if (editRes?.detail || Object.keys(editRes).length === 0) {
          showErrorNotification(t("defaultInformationStrings.saveFailed"));
          await refreshDefaultCIPFromServer();
          return;
        }
        // Only update store if save is successful
        dispatch(storeDefaultInformation(editRes));

        // don't show success notification in case auto-save
        if (Object.keys(newChangedValues).length === 0) {
          showSuccessNotification(t("defaultInformationStrings.saveSuccess"));
        }
      } catch (editError) {
        showErrorNotification(t("defaultInformationStrings.saveFailed"));
        await refreshDefaultCIPFromServer();
      } finally {
        setChangedCIPValues({});
        setIsSaving(false);
      }
    };

    useEffect(() => {
      changedCIPValuesRef.current = changedCIPValues;
    }, [changedCIPValues]);

    useEffect(() => {
      defaultCIPValuesRef.current = defaultCIPValues;
    }, [defaultCIPValues]);

    useEffect(() => {
      setCIPSequenceTextArr(
        getCIPSequenceTextArrFromCIPSequenceID(defaultCIPValues?.cip_sequence),
      );
    }, [defaultCIPValues?.cip_sequence]);

    useEffect(() => {
      if (
        !Array.isArray(plantChemicalLibrary) ||
        plantChemicalLibrary.length === 0
      ) {
        return;
      }

      if (!hasDefaultCIPInitialMount.current) {
        hasDefaultCIPInitialMount.current = true;
        return;
      }

      refreshDefaultCIPFromServer();
    }, [plantChemicalLibrary]);

    useEffect(() => {
      if (cips === null) {
        setErrors((prev) => ({
          ...prev,
          acid_cip_temp: null,
          alkali_cip_temp: null,
          additive_alkali_temp: null,
          additive_acid_temp: null,
        }));
      }
    }, [temperature, cips]);

    useEffect(() => {
      setChangedCIPValues({});
      setErrors(INITIAL_DEFAULT_CIP_ERROR_STATE);
    }, [selectedTrain]);

    useEffect(() => {
      if (setMandatoryComponentsStatus == null) return;
      const hasChanges = Object.keys(changedCIPValues).length > 0;
      const defaultCIPInfoState = {
        fulfilled: !hasChanges && noError && !hasMissingRequiredFields,
        changed: hasChanges,
        error: !noError || hasMissingRequiredFields,
      };
      setMandatoryComponentsStatus((prev) => {
        return { ...prev, defaultCIPInfo: defaultCIPInfoState };
      });
    }, [changedCIPValues, noError, hasMissingRequiredFields, fileId]);

    const initialLoadRef = useRef(true);
    const prevNoOfRONFElementsRef = useRef(noOfRONFElements);

    useEffect(() => {
      if (isNullOrUndefined(noOfRONFElements)) return;
      if (initialLoadRef.current) {
        initialLoadRef.current = false;

        const prevTrainConfig = JSON.parse(
          getLocalStorageItem("prevTrainConfig"),
        );

        if (!prevTrainConfig) {
          const { NUM_PRESSURE_VESSELS, NUM_ELEMENTS_PER_PV } = STAGE_DEFAULTS;
          prevNoOfRONFElementsRef.current =
            NUM_PRESSURE_VESSELS * NUM_ELEMENTS_PER_PV;
        } else {
          if (selectedStage === "All") {
            let totalElements = 0;

            Object.values(
              prevTrainConfig[selectedTrain]?.[selectedPass],
            ).forEach((stageElements) => {
              totalElements += stageElements;
            });
            prevNoOfRONFElementsRef.current = totalElements;
          } else {
            prevNoOfRONFElementsRef.current =
              prevTrainConfig[selectedTrain]?.[selectedPass]?.[selectedStage];
          }
        }
      }

      const { NUM_PRESSURE_VESSELS, NUM_ELEMENTS_PER_PV } = STAGE_DEFAULTS;
      const { ACID, ALKALI, ADDITIVE_ACID, ADDITIVE_ALKALI } =
        DEFAULT_CIP_AMOUNT_PER_ELEMENT;
      const defaultNoOfElements = NUM_PRESSURE_VESSELS * NUM_ELEMENTS_PER_PV;

      const currNoOfElements = Number(noOfRONFElements) || defaultNoOfElements;

      const currAcid = (ACID * currNoOfElements).toFixed(2);
      const currAlkali = (ALKALI * currNoOfElements).toFixed(2);
      const currAdditiveAcid = (ADDITIVE_ACID * currNoOfElements).toFixed(2);
      const currAdditiveAlkali = (ADDITIVE_ALKALI * currNoOfElements).toFixed(
        2,
      );

      // Always update acid, alkali, additive acid, and additive alkali values when number of elements changes & convert to the current mass unit
      const newChangedCIPValues = {
        acid_used_per_cip: unitConverter(currAcid, mass),
        alkali_used_per_cip: unitConverter(currAlkali, mass),
        additive_acid_used_per_cip: unitConverter(currAdditiveAcid, mass),
        additive_alkali_used_per_cip: unitConverter(currAdditiveAlkali, mass),
      };
      handleSave(newChangedCIPValues);
      prevNoOfRONFElementsRef.current = noOfRONFElements;
    }, [noOfRONFElements]);

    return (
      <div className="ro-card-wrapper default-cip-information-card">
        {isSaving && (
          <div className="circular-loader-overlay">
            <CircularLoader />
          </div>
        )}
        <div>
          <div className="ro-card-header">
            <div className="new-badge-inline-wrapper">
              <div className="info-section">
                <div className="teal-heading">
                  {t("defaultInformationStrings.defaultInformation")}
                </div>
                {!disabled && (
                  <IconWithTooltip
                    label={t("helpInformation.defaultCIPInformationHelp")}
                  />
                )}
              </div>
              <NewBadgeIcon className="new-badge" />
            </div>
            <WPTertiaryButton
              variant="outlined"
              label={t("defaultInformationStrings.save")}
              onClick={() => handleSave()}
              icon={<SaveIcon width="16" height="16" />}
              iconPlacement="before"
              size="small"
              disabled={getSaveButtonState() || disabled}
            />
          </div>

          <CIPSequence
            cipSequenceTextArr={cipSequenceTextArr}
            setCIPSequenceTextArr={setCIPSequenceTextArr}
            setChangedCIPValues={setChangedCIPValues}
            errors={errors}
            handleInputChange={handleInputChange}
            onFieldChange={handleSectionFieldChange}
            onMandatoryValidationChange={setHasMissingRequiredFields}
            disabled={disabled}
            existingCIPState={changedCIPValues}
          />
        </div>
      </div>
    );
  },
);

export default DefaultCIPInformation;
